/**
 * ChartContainer holds a chart and lets it be zoomed and panned horizontally: pinch and swipe on
 * touch screens, the scroll wheel and dragging with the mouse on desktop, double click to reset;
 * buttons above it pan, zoom in, out and back
 */

import React, { useEffect, useRef, useState } from 'react'
import { Button, ButtonGroup } from 'rsuite'
import Icon from './Icon'

/**
 * Pixels a pointer moves before a press counts as dragging rather than a click on a point
 * @type {number}
 */
const DRAG_THRESHOLD = 5

/**
 * Zoom per pixel of scroll wheel movement
 * @type {number}
 */
const WHEEL_ZOOM = 0.0015

/**
 * Pixels per line, for scroll wheels that report lines
 * @type {number}
 */
const WHEEL_LINE = 16

/**
 * Width of the window after a click on zoom in; zoom out doubles it
 * @type {number}
 */
const BUTTON_ZOOM = 0.5

/**
 * Part of the window a click on pan moves it
 * @type {number}
 */
const BUTTON_PAN = 0.5

/**
 * Horizontal distance in pixels two fingers need for a pinch, so small distances don't jump
 * @type {number}
 */
const PINCH_MIN_DISTANCE = 20

/**
 * Keep a window within the range of the chart, at least minRange wide
 * @param {number} min start of the wanted window
 * @param {number} max end of the wanted window
 * @param {Object} domain range of the chart: min, max and minRange
 * @returns {Object|null} window with min, max and whether it sticks to the end; null for all of it
 */
const clampWindow = ( min, max, domain ) =>
{
  const full = domain.max - domain.min
  const range = Math.max( Math.min( max - min, full ), Math.min( domain.minRange, full ) )

  if ( range >= full ) return null

  const start = Math.min( Math.max( min, domain.min ), domain.max - range )
  return { min: start, max: start + range, pinned: start + range >= domain.max }
}

/**
 * Get the shown window; a window at the end moves along when laps are added
 * @param {Object|null} zoom chosen window, null for all of it
 * @param {Object} domain range of the chart: min, max and minRange
 * @returns {Object} shown window with min and max
 */
const getWindow = ( zoom, domain ) =>
{
  if ( !zoom ) return { min: domain.min, max: domain.max }

  const range = zoom.max - zoom.min
  const shown = zoom.pinned ? clampWindow( domain.max - range, domain.max, domain ) : clampWindow( zoom.min, zoom.max, domain )
  return shown || { min: domain.min, max: domain.max }
}

/**
 * Render a zoomable chart
 * @param {Object} props component props
 * @param {number} props.min first value on the horizontal axis
 * @param {number} props.max last value on the horizontal axis
 * @param {number} props.minRange narrowest window to zoom in to
 * @param {number} props.left width of the chart area left of the plot, as in chartArea.left
 * @param {number} props.right width of the chart area right of the plot, as in chartArea.right
 * @param {string} props.className class names of the container
 * @param {string} props.id id of the container
 * @param {*} props.footer shown centred below the chart, such as the source of its data
 * @param {boolean} props.zoom whether the chart can be zoomed and panned
 * @param {Function} props.onViewChange called when the chart is zoomed or panned, such as to close
 *   a tooltip that no longer points at its point
 * @param {Function} props.children renders the chart for a window with min and max, null when it cannot zoom
 * @returns {JSX.Element} container with the chart
 */
const ChartContainer = ( { min = 0, max = 0, minRange = 1, left = 0, right = 0, className = '', id = undefined, footer = null, zoom: canZoom = true, onViewChange = null, children } ) =>
{
  const ref = useRef( null )
  const [ zoom, setZoom ] = useState( null )

  // tell the chart its window changed
  useEffect( () =>
  {
    if ( onViewChange ) onViewChange()
  }, [ zoom ] )
  const zoomable = canZoom && max > min
  const domain = { min, max, minRange }
  const shown = getWindow( zoom, domain )

  /**
   * Zoom in or out around the middle of the shown window
   * @param {number} factor new width relative to the current one, below 1 zooms in
   * @returns {void}
   */
  const zoomBy = ( factor ) =>
  {
    const middle = ( shown.min + shown.max ) / 2
    const half = ( shown.max - shown.min ) * factor / 2
    setZoom( clampWindow( middle - half, middle + half, domain ) )
  }

  /**
   * Move the shown window by part of its width
   * @param {number} direction -1 for earlier values, 1 for later ones
   * @returns {void}
   */
  const panBy = ( direction ) =>
  {
    const shift = ( shown.max - shown.min ) * BUTTON_PAN * direction
    setZoom( clampWindow( shown.min + shift, shown.max + shift, domain ) )
  }

  // latest values for the event listeners, which are bound once
  const state = useRef( {} )
  state.current = { domain, shown, left, right, zoomable, frame: state.current.frame }

  useEffect( () =>
  {
    const el = ref.current
    if ( !el ) return

    // pointers on the chart by id, with their start and last horizontal position
    const pointers = new Map()
    let dragged = false

    /**
     * Get the plot area: its left edge on screen and its width
     * @returns {Object} left and width in pixels
     */
    const getArea = () =>
    {
      const rect = el.getBoundingClientRect()
      return { left: rect.left + state.current.left, width: Math.max( 1, rect.width - state.current.left - state.current.right ) }
    }

    /**
     * Show a new window, drawn on the next frame
     * @param {number} from start of the window
     * @param {number} to end of the window
     * @returns {boolean} whether the window changed
     */
    const show = ( from, to ) =>
    {
      const { domain, shown } = state.current
      const next = clampWindow( from, to, domain )
      const now = next || { min: domain.min, max: domain.max }

      if ( Math.abs( now.min - shown.min ) < 1e-9 && Math.abs( now.max - shown.max ) < 1e-9 ) return false

      // keep calculating from the new window until it is drawn
      state.current.shown = now
      cancelAnimationFrame( state.current.frame )
      state.current.frame = requestAnimationFrame( () => setZoom( next ) )
      return true
    }

    /**
     * Zoom around a point on screen
     * @param {number} x horizontal position on screen that stays in place
     * @param {number} factor new width relative to the current one, below 1 zooms in
     * @returns {boolean} whether the window changed
     */
    const zoomAt = ( x, factor ) =>
    {
      const { shown } = state.current
      const area = getArea()
      const ratio = Math.min( Math.max( ( x - area.left ) / area.width, 0 ), 1 )
      const range = shown.max - shown.min
      const anchor = shown.min + ratio * range
      return show( anchor - ratio * range * factor, anchor + ( 1 - ratio ) * range * factor )
    }

    /**
     * Move the window by a number of pixels
     * @param {number} dx pixels to move, positive moves to earlier values
     * @returns {boolean} whether the window changed
     */
    const pan = ( dx ) =>
    {
      const { shown } = state.current
      const shift = dx / getArea().width * ( shown.max - shown.min )
      return show( shown.min - shift, shown.max - shift )
    }

    /**
     * Zoom with the scroll wheel, or pan with a sideways scroll; the page scrolls when nothing changes
     * @param {WheelEvent} e wheel event
     * @returns {void}
     */
    const onWheel = ( e ) =>
    {
      if ( !state.current.zoomable ) return

      const unit = e.deltaMode === 1 ? WHEEL_LINE : 1
      const changed = Math.abs( e.deltaX ) > Math.abs( e.deltaY )
        ? pan( -e.deltaX * unit )
        : zoomAt( e.clientX, Math.exp( e.deltaY * unit * WHEEL_ZOOM ) )

      if ( changed ) e.preventDefault()
    }

    /**
     * Start tracking a finger or the left mouse button
     * @param {PointerEvent} e pointer event
     * @returns {void}
     */
    const onPointerDown = ( e ) =>
    {
      if ( !state.current.zoomable || ( e.pointerType === 'mouse' && e.button !== 0 ) ) return
      pointers.set( e.pointerId, { start: e.clientX, x: e.clientX } )
      dragged = false
    }

    /**
     * Pan with one pointer, zoom with two
     * @param {PointerEvent} e pointer event
     * @returns {void}
     */
    const onPointerMove = ( e ) =>
    {
      const pointer = pointers.get( e.pointerId )
      if ( !pointer ) return

      // a press only becomes a drag after moving a bit, so clicks on points keep working
      if ( !dragged && Math.abs( e.clientX - pointer.start ) < DRAG_THRESHOLD ) return
      if ( !dragged )
      {
        dragged = true
        el.classList.add( 'dragging' )
        el.setPointerCapture( e.pointerId )
      }

      if ( pointers.size === 1 )
      {
        pan( e.clientX - pointer.x )
      }
      else if ( pointers.size === 2 )
      {
        const other = [ ...pointers.entries() ].find( ( [ key ] ) => key !== e.pointerId )[ 1 ]
        const before = Math.abs( pointer.x - other.x )
        const after = Math.abs( e.clientX - other.x )
        if ( before >= PINCH_MIN_DISTANCE && after >= PINCH_MIN_DISTANCE )
        {
          // zoom around the middle of the fingers, so the content follows them
          zoomAt( ( pointer.x + other.x ) / 2, before / after )
        }
      }

      pointer.x = e.clientX
    }

    /**
     * Stop tracking a pointer
     * @param {PointerEvent} e pointer event
     * @returns {void}
     */
    const onPointerUp = ( e ) =>
    {
      pointers.delete( e.pointerId )
      if ( !pointers.size ) el.classList.remove( 'dragging' )
    }

    /**
     * Ignore the click that ends a drag, so it doesn't select a point
     * @param {MouseEvent} e click event
     * @returns {void}
     */
    const onClick = ( e ) =>
    {
      if ( !dragged ) return
      dragged = false
      e.stopPropagation()
      e.preventDefault()
    }

    /**
     * Show all of the chart again
     * @returns {void}
     */
    const onDoubleClick = () => show( state.current.domain.min, state.current.domain.max )

    el.addEventListener( 'wheel', onWheel, { passive: false } )
    el.addEventListener( 'pointerdown', onPointerDown )
    el.addEventListener( 'pointermove', onPointerMove )
    el.addEventListener( 'pointerup', onPointerUp )
    el.addEventListener( 'pointercancel', onPointerUp )
    el.addEventListener( 'click', onClick, true )
    el.addEventListener( 'dblclick', onDoubleClick )

    return () =>
    {
      cancelAnimationFrame( state.current.frame )
      el.removeEventListener( 'wheel', onWheel )
      el.removeEventListener( 'pointerdown', onPointerDown )
      el.removeEventListener( 'pointermove', onPointerMove )
      el.removeEventListener( 'pointerup', onPointerUp )
      el.removeEventListener( 'pointercancel', onPointerUp )
      el.removeEventListener( 'click', onClick, true )
      el.removeEventListener( 'dblclick', onDoubleClick )
    }
  }, [] )

  return (
    <div className={ `chart-container ${ className }` } id={ id }>
      { zoomable && (
        <div className="chart-toolbar">
          <span className="chart-control-label">Pan</span>
          <ButtonGroup>
            <Button aria-label="Pan to earlier" title="Pan to earlier" disabled={ !zoom || shown.min <= domain.min } onClick={ () => panBy( -1 ) }>
              <Icon icon="page-prev"/>
            </Button>
            <Button aria-label="Pan to later" title="Pan to later" disabled={ !zoom || shown.max >= domain.max } onClick={ () => panBy( 1 ) }>
              <Icon icon="page-next"/>
            </Button>
          </ButtonGroup>
          <span className="chart-control-label">Zoom</span>
          <ButtonGroup>
            <Button aria-label="Zoom in" title="Zoom in" disabled={ shown.max - shown.min <= minRange } onClick={ () => zoomBy( BUTTON_ZOOM ) }>
              <Icon icon="plus"/>
            </Button>
            <Button aria-label="Zoom out" title="Zoom out" disabled={ !zoom } onClick={ () => zoomBy( 1 / BUTTON_ZOOM ) }>
              <Icon icon="minus"/>
            </Button>
            <Button aria-label="Reset zoom" title="Reset zoom" disabled={ !zoom } onClick={ () => setZoom( null ) }>
              <Icon icon="refresh"/>
            </Button>
          </ButtonGroup>
        </div>
      ) }
      <div className={ `chart-plot ${ zoomable ? 'zoomable' : '' }` } ref={ ref }>
        { children( zoomable ? shown : null ) }
      </div>
      { footer && <div className="chart-footer">{ footer }</div> }
    </div>
  )
}

export default ChartContainer
